import { useMemo, useEffect } from "react";
import { Play, Pause, Rewind, FastForward, Volume2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useStore } from "@/store/useStore";
import { getAudio } from "@/audio";

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

export default function AudioBar() {
  const { playing, setPlaying, volume, setVolume, progress, setProgress, setDuration, duration } = useStore();
  const heights = useMemo(() => new Array(64).fill(0).map(() => 20 + Math.random() * 80), []);

  // Wire the shared <audio> element (real TIME commentary track).
  useEffect(() => {
    const audio = getAudio();
    const onMeta = () => setDuration(audio.duration || 78);
    const onTime = () => setProgress(audio.currentTime);
    const onEnd = () => setPlaying(false);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    if (audio.readyState >= 1) onMeta();
    return () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, [setDuration, setProgress, setPlaying]);

  useEffect(() => {
    const audio = getAudio();
    if (playing) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, [playing, setPlaying]);

  useEffect(() => {
    getAudio().volume = Math.max(0, Math.min(1, volume / 100));
  }, [volume]);

  const seek = (v) => {
    getAudio().currentTime = v;
    setProgress(v);
  };
  const skip = (d) => seek(Math.max(0, Math.min(duration, progress + d)));

  return (
    <footer
      data-testid="audio-commentary-panel"
      className="glass fixed inset-x-0 bottom-0 z-50 border-x-0 border-b-0 px-3 py-2.5 sm:px-6 sm:py-3"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 lg:flex-row lg:items-center lg:gap-5">
        {/* label */}
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-[#d4af37]">Audio Commentary · TIME Inventions Series</div>
          <div className="truncate text-xs font-medium text-slate-200 sm:text-sm">
            Emma Barker Bonomo — Evaluating Breakthrough Hardware
          </div>
        </div>

        {/* waveform */}
        <div
          data-testid="audio-waveform-visualizer"
          className={`flex h-8 flex-1 items-center justify-center gap-[2px] overflow-hidden ${playing ? "" : "wave-paused"}`}
        >
          {heights.map((h, i) => (
            <span
              key={i}
              className="wave-bar"
              style={{ height: `${h}%`, animationDelay: `${(i % 12) * 0.08}s` }}
            />
          ))}
        </div>

        {/* controls */}
        <div className="flex items-center gap-3">
          <button onClick={() => skip(-15)} data-testid="audio-rewind" className="text-slate-400 hover:text-white">
            <Rewind className="h-4 w-4" />
          </button>
          <button
            onClick={() => setPlaying(!playing)}
            data-testid="audio-play-pause-button"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#00f0ff] to-[#a855f7] text-[#05070d] shadow-[0_0_20px_rgba(0,240,255,0.4)]"
          >
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-[1px]" />}
          </button>
          <button onClick={() => skip(15)} data-testid="audio-forward" className="text-slate-400 hover:text-white">
            <FastForward className="h-4 w-4" />
          </button>

          <span className="mono hidden text-[0.72rem] text-slate-400 sm:inline">
            {fmt(progress)} / {fmt(duration)}
          </span>

          <div className="hidden w-40 items-center lg:flex" data-testid="audio-scrubber">
            <Slider value={[progress]} min={0} max={duration} step={1} onValueChange={([v]) => seek(v)} />
          </div>

          <div className="flex items-center gap-1.5">
            <button onClick={() => setVolume(volume > 0 ? 0 : 72)} className="text-slate-400 hover:text-white">
              {volume > 0 ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
            <div className="w-20" data-testid="audio-volume-slider">
              <Slider value={[volume]} min={0} max={100} step={1} onValueChange={([v]) => setVolume(v)} />
            </div>
          </div>
        </div>
      </div>

      {/* mobile scrubber */}
      <div className="mt-1.5 flex items-center gap-2 lg:hidden">
        <span className="mono text-[0.72rem] text-slate-400">{fmt(progress)}</span>
        <div className="flex-1">
          <Slider value={[progress]} min={0} max={duration} step={1} onValueChange={([v]) => seek(v)} />
        </div>
        <span className="mono text-[0.72rem] text-slate-400">{fmt(duration)}</span>
      </div>
    </footer>
  );
}
